// ecommerce-frontend/src/services/api.js
// VERSION FINALE avec API Favoris

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';


// Instance Axios avec configuration par défaut
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token à chaque requête
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer le rafraîchissement du token
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const response = await axios.post(`${API_BASE_URL}/users/token/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;
        localStorage.setItem('access_token', access);

        originalRequest.headers.Authorization = `Bearer ${access}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// API D'AUTHENTIFICATION
// ============================================
export const authAPI = {
  register: async (userData) => {
    const response = await axiosInstance.post('/users/register/', userData);
    return response.data;
  },

  login: async (credentials) => {
    const response = await axiosInstance.post('/users/login/', credentials);
    const { tokens, user } = response.data;

    localStorage.setItem('access_token', tokens.access);
    localStorage.setItem('refresh_token', tokens.refresh);
    localStorage.setItem('user', JSON.stringify(user));

    return response.data;
  },

  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('access_token');
  },

  forgotPassword: async (data) => {
    const response = await axiosInstance.post('/users/password-reset/', data);
    return response.data;
  },

  resetPassword: async (data) => {
    const response = await axiosInstance.post('/users/password-reset-confirm/', data);
    return response.data;
  },

  changePassword: async (data) => {
    const response = await axiosInstance.post('/users/change-password/', data);
    return response.data;
  },

  updateProfile: async (data) => {
    const config = data instanceof FormData ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    
    const response = await axiosInstance.patch('/users/profile/', data, config);
    return response.data;
  },

  suspendAccount: async () => {
    const response = await axiosInstance.post('/users/suspend-account/');
    return response.data;
  },

  deleteAccount: async () => {
    const response = await axiosInstance.delete('/users/delete-account/');
    return response.data;
  },
};

// ============================================
// API PRODUITS
// ============================================
export const productsAPI = {
  getProducts: async (params = {}) => {
    const response = await axiosInstance.get('/products/', { params });
    return response.data;
  },

  getProduct: async (id) => {
    const response = await axiosInstance.get(`/products/${id}/`);
    return response.data;
  },

  create: async (productData) => {
    const config = productData instanceof FormData ? {
      headers: { 'Content-Type': 'multipart/form-data' }
    } : {};
    const response = await axiosInstance.post('/products/', productData, config);
    return response.data;
  },

  update: async (id, productData) => {
    const config = productData instanceof FormData ? {
      headers: { 'Content-Type': 'multipart/form-data' }
    } : {};
    const response = await axiosInstance.put(`/products/${id}/`, productData, config);
    return response.data;
  },
  
  updateStock: async (id, stock) => {
    const response = await axiosInstance.patch(`/products/${id}/`, { stock });
    return response.data;
  },

  delete: async (id) => {
    const response = await axiosInstance.delete(`/products/${id}/`);
    return response.data;
  },

  search: async (query) => {
    const response = await axiosInstance.get('/products/', { params: { search: query } });
    return response.data;
  },

  getVendorProducts: async (params = {}) => {
    const response = await axiosInstance.get('/products/vendor/', { params });
    return response.data;
  },
  
  getOutOfStockProducts: async () => {
    return productsAPI.getVendorProducts({ stock: 0 });
  },

  getCategories: async () => {
    const response = await axiosInstance.get('/products/categories/');
    return response.data;
  },
};

// ============================================
// ✅ NOUVEAU: API FAVORIS
// ============================================
export const favoritesAPI = {
  // Liste des favoris de l'utilisateur
  getFavorites: async () => {
    const response = await axiosInstance.get('/products/favorites/');
    return response.data;
  },

  // Vérifier si un produit est en favoris
  checkFavorite: async (productId) => {
    const response = await axiosInstance.get('/products/favorites/check/', {
      params: { product_id: productId }
    });
    return response.data.is_favorite;
  },

  // Ajouter/Retirer des favoris (toggle)
  toggleFavorite: async (productId) => {
    const response = await axiosInstance.post('/products/favorites/toggle/', {
      product_id: productId
    });
    return response.data;
  }
};

// ============================================
// API COMMANDES
// ============================================
export const ordersAPI = {
  getAll: async () => {
    const response = await axiosInstance.get('/orders/');
    return response.data;
  },

  getById: async (id) => {
    const response = await axiosInstance.get(`/orders/${id}/`);
    return response.data;
  },

  create: async (orderData) => {
    const response = await axiosInstance.post('/orders/', orderData);
    return response.data;
  },

  updateStatus: async (id, status) => {
    const response = await axiosInstance.patch(`/orders/${id}/`, { status });
    return response.data;
  },

  getVendorOrders: async () => {
    const response = await axiosInstance.get('/orders/vendor/');
    return response.data;
  },
};

// ============================================
// API PANIER
// ============================================
export const cartAPI = {
  getCart: async () => {
    const response = await axiosInstance.get('/cart/');
    return response.data;
  },

  addToCart: async (productId, quantity = 1) => {
    const response = await axiosInstance.post('/cart/add/', {
      product_id: productId,
      quantity,
    });
    return response.data;
  },

  updateCartItem: async (itemId, quantity) => {
    const response = await axiosInstance.patch(`/cart/items/${itemId}/`, { quantity });
    return response.data;
  },

  removeFromCart: async (itemId) => {
    const response = await axiosInstance.delete(`/cart/items/${itemId}/delete/`);
    return response.data;
  },

  clearCart: async () => {
    const response = await axiosInstance.delete('/cart/clear/');
    return response.data;
  },
};

// ============================================
// API CATÉGORIES
// ============================================
export const categoriesAPI = {
  getAll: async () => {
    const response = await axiosInstance.get('/products/categories/');
    return response.data;
  },

  getById: async (id) => {
    const response = await axiosInstance.get(`/products/categories/${id}/`);
    return response.data;
  },
};

export default axiosInstance;